import { Link } from "react-router-dom";
import { ArrowRight, Phone, Sailboat, Waves } from "lucide-react";
import { SITE, PHOTOS, BOATING_OFFERINGS } from "../data/content";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";

export default function Boating() {
  return (
    <main data-testid="boating-page">
      <PageHero
        image={PHOTOS.marinaPontoonDocked}
        eyebrow="Boating & Water Activities"
        title="Rent. Ride. Relax."
        subtitle="Pontoons, kayaks, paddleboards, tubes and more — everything you need to make a Tenkiller day on the water."
      />

      {/* INTRO */}
      <section className="py-24 bg-[var(--ec-cream)]" data-testid="boating-intro">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--ec-lake)] mb-3">
              On the water
            </div>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--ec-navy)] leading-[1.05]">
              No boat? No problem — we've got you covered.
            </h2>
            <p className="mt-6 text-[var(--ec-charcoal)]/80 leading-relaxed">
              From a quiet morning paddle to a full day on a pontoon with friends, Elk Creek has the gear and the lake to make it happen. Availability varies by day and season — give us a call to confirm what's open and to reserve.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={`tel:${SITE.phoneRaw}`}
                data-testid="boating-call-button"
                className="inline-flex items-center gap-2 bg-[var(--ec-navy)] text-white hover:bg-[var(--ec-lake)] font-semibold px-7 py-4 rounded-full btn-lift"
              >
                <Phone className="w-4 h-4" /> Call {SITE.phoneDisplay}
              </a>
              <Link
                to="/contact"
                data-testid="boating-contact-button"
                className="inline-flex items-center gap-2 border border-[var(--ec-navy)] text-[var(--ec-navy)] hover:bg-[var(--ec-navy)] hover:text-white font-semibold px-7 py-4 rounded-full btn-lift"
              >
                Ask About Availability
              </Link>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={150}>
            <div className="grid grid-cols-2 gap-3">
              <div className="img-zoom rounded-2xl overflow-hidden shadow-md card-lift aspect-[4/5]">
                <img src={PHOTOS.tubing} alt="Tubing on Lake Tenkiller" className="w-full h-full object-cover" />
              </div>
              <div className="img-zoom rounded-2xl overflow-hidden shadow-md card-lift aspect-[4/5] mt-8">
                <img src={PHOTOS.kayakYellow} alt="Yellow kayak on the cove" className="w-full h-full object-cover" />
              </div>
              <div className="img-zoom rounded-2xl overflow-hidden shadow-md card-lift aspect-[4/5] -mt-6">
                <img src={PHOTOS.pontoonCruise} alt="Pontoon cruising the lake" className="w-full h-full object-cover" />
              </div>
              <div className="img-zoom rounded-2xl overflow-hidden shadow-md card-lift aspect-[4/5] mt-2">
                <img src={PHOTOS.kayakPov} alt="Kayak paddle on the water" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OFFERINGS */}
      <section className="py-24 bg-white" data-testid="boating-offerings">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeading
            eyebrow="What's available"
            title="Rentals & water-sport options."
            subtitle="All rentals & activities — contact Elk Creek for availability, pricing and reservations."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {BOATING_OFFERINGS.map((o, i) => (
              <Reveal key={o.title} delay={i * 70}>
                <div className="h-full bg-[var(--ec-cream)] rounded-2xl p-7 border border-[var(--ec-sand)] card-lift" data-testid={`boating-offering-${i}`}>
                  <div className="w-11 h-11 rounded-full bg-[var(--ec-lake)]/10 flex items-center justify-center mb-5">
                    {i % 2 === 0 ? (
                      <Sailboat className="w-5 h-5 text-[var(--ec-lake)]" />
                    ) : (
                      <Waves className="w-5 h-5 text-[var(--ec-lake)]" />
                    )}
                  </div>
                  <h3 className="font-display text-2xl text-[var(--ec-navy)]">{o.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--ec-charcoal)]/75">{o.body}</p>
                  <a
                    href={`tel:${SITE.phoneRaw}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--ec-navy)] underline-offset-4 hover:underline"
                    data-testid={`boating-offering-${i}-call`}
                  >
                    Call for availability <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PHOTO STRIP */}
      <section className="py-24 bg-[var(--ec-sand-soft)]" data-testid="boating-photos">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <SectionHeading
            eyebrow="From the lake"
            title="A few snapshots of Tenkiller days."
            align="center"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              [PHOTOS.pontoonClassic, "Pontoon boat on Lake Tenkiller"],
              [PHOTOS.funSelfie, "Friends enjoying a day on the lake"],
              [PHOTOS.pontoonCruise, "Pontoon cruising the lake"],
              [PHOTOS.tubing, "Tubing behind a boat on Lake Tenkiller"],
              [PHOTOS.kayakPov, "Kayak paddle on the water"],
              [PHOTOS.lakeWake, "Boat wake across Lake Tenkiller"],
            ].map(([src, alt], i) => (
              <Reveal key={src} delay={i * 60}>
                <div className="img-zoom rounded-2xl overflow-hidden shadow-md card-lift aspect-[4/3]">
                  <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[var(--ec-navy)] text-white" data-testid="boating-cta">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 text-center">
          <h2 className="font-display text-4xl sm:text-5xl text-white">Ready for a day on the water?</h2>
          <p className="mt-5 text-white/75 max-w-xl mx-auto">
            Reserve early — rentals book up on weekends and holidays. Call {SITE.phoneDisplay} or send us a message.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a
              href={`tel:${SITE.phoneRaw}`}
              data-testid="boating-cta-call"
              className="inline-flex items-center gap-2 bg-[var(--ec-lake)] hover:bg-[var(--ec-lake-dark)] text-white font-semibold px-7 py-4 rounded-full btn-lift"
            >
              <Phone className="w-4 h-4" /> Call {SITE.phoneDisplay}
            </a>
            <Link
              to="/contact"
              data-testid="boating-cta-contact"
              className="inline-flex items-center gap-2 border border-white/50 text-white hover:bg-white/10 font-semibold px-7 py-4 rounded-full btn-lift"
            >
              Send a Message
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
